import { LitElement } from 'lit';
import { Mutate, StoreApi, StoreMutatorIdentifier } from 'zustand/vanilla';

type TConstructor<T> = new (...args: unknown[]) => T;

type TZustandToComponentProps<R, S> = {
  mapState: (state: S, component?: LitElement) => R;
};

export class ZustandStateEntity<S, R extends Record<string, unknown>> {
  private readonly _mapStateEntries: (
    state: S,
    component?: LitElement,
  ) => [string, any][];

  constructor(mapState: TZustandToComponentProps<R, S>['mapState']) {
    this._mapStateEntries = (state: S, component?: LitElement) =>
      Object.entries(mapState(state, component));
  }

  mapOver<T>(state: S, component?: LitElement, rtn: T = {} as T): T {
    this._mapStateEntries(state, component).forEach(([key, value]) => {
      rtn[key] = typeof value === 'function' ? value(state) : value;
    });
    return rtn;
  }
}

export const ZustandStateToComponent =
  <S, T extends TConstructor<LitElement>>(
    store: Mutate<StoreApi<S>, [StoreMutatorIdentifier, unknown][]>,
  ) =>
  <R extends Record<string, unknown>>(
    options: Partial<TZustandToComponentProps<R, S>>,
    BaseElement: T,
  ): T => {
    const mapState = options.mapState;

    const stateMapper = new ZustandStateEntity<S, R>(mapState);

    return class extends BaseElement {
      private storeUnsubscribe: () => void;

      componentObject?: unknown;

      constructor(...params: any[]) {
        super(...params);
      }

      connectedCallback() {
        super.connectedCallback();

        this.storeUnsubscribe = store.subscribe(() =>
          this.__store__stateChanged(),
        );
        this.__store__stateChanged();
      }

      private __store__stateChanged() {
        this.__mapState();
      }

      private __mapState() {
        if (this.componentObject && !store.getState()) {
          return;
        }
        stateMapper.mapOver(store.getState(), this, this);
      }

      disconnectedCallback() {
        if (this.storeUnsubscribe) {
          this.storeUnsubscribe();
        }
        super.disconnectedCallback();
      }
    };
  };
